import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLValidation = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Best Practices</div>
                <h1>HTML Validation & Error Detection</h1>
                <p className="hero-lead">
                    Learn how to validate HTML, catch errors before deployment, and ensure cross-browser
                    compatibility. Master validation tools and debugging techniques.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Validate HTML?</h2>
                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> Find Errors</h3>
                        <p>Catch syntax errors and typos</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Cross-Browser</h3>
                        <p>Ensure compatibility</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Accessibility</h3>
                        <p>Improve accessibility compliance</p>
                    </div>
                    <div className="benefit-card">
                        <h3> SEO</h3>
                        <p>Better search engine rankings</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>W3C Markup Validation Service</h2>
                <p>
                    The official HTML validator from W3C: <strong>https://validator.w3.org/</strong>
                </p>

                <h3>Three Ways to Validate:</h3>
                <ol>
                    <li><strong>By URL:</strong> Enter your website URL</li>
                    <li><strong>By File Upload:</strong> Upload HTML file</li>
                    <li><strong>By Direct Input:</strong> Paste HTML code</li>
                </ol>

                <h3>Understanding Validation Results</h3>
                <ul>
                    <li><span style={{ color: 'red' }}> Error:</span> Must fix (invalid HTML)</li>
                    <li><span style={{ color: 'orange' }}> Warning:</span> Should fix (potential issues)</li>
                    <li><span style={{ color: 'blue' }}> Info:</span> Optional (suggestions)</li>
                    <li><span style={{ color: 'green' }}>Success:</span> Document is valid</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Common HTML Errors</h2>

                <h3>Error 1: Missing DOCTYPE</h3>
                <CodeBlock className="error" language="html" code={`<!--  Missing DOCTYPE --&gt;
<html>
<head>
    <title>Page</title>
</head>
</html>`} />
                <CodeBlock language="html" code={`<!-- Include DOCTYPE --&gt;
<!DOCTYPE html>
<html>
<head>
    <title>Page</title>
</head>
</html>`} />

                <h3>Error 2: Unclosed Tags</h3>
                <CodeBlock className="error" language="html" code={`<!--  Unclosed div --&gt;
<div class="container">
    <p>Content</p>
<!-- Missing </div> --&gt;`} />
                <CodeBlock language="html" code={`<!-- Properly closed --&gt;
<div class="container">
    <p>Content</p>
</div>`} />

                <h3>Error 3: Nested Tags Incorrectly</h3>
                <CodeBlock className="error" language="html" code={`<!--  Wrong nesting --&gt;
<p>This is <strong>bold text</p></strong>

<!--  Block in inline --&gt;
<span><div>Content</div></span>`} />
                <CodeBlock language="html" code={`<!-- Correct nesting --&gt;
<p>This is <strong>bold text</strong></p>

<!-- Inline in block --&gt;
<div><span>Content</span></div>`} />

                <h3>Error 4: Duplicate IDs</h3>
                <CodeBlock className="error" language="html" code={`<!--  Duplicate IDs --&gt;
<div id="content">First</div>
<div id="content">Second</div>`} />
                <CodeBlock language="html" code={`<!-- Unique IDs --&gt;
<div id="content-1">First</div>
<div id="content-2">Second</div>

<!-- Or use classes for multiple elements --&gt;
<div class="content">First</div>
<div class="content">Second</div>`} />

                <h3>Error 5: Missing Required Attributes</h3>
                <CodeBlock className="error" language="html" code={`<!--  Missing alt attribute --&gt;
<img src="photo.jpg">

<!--  Missing for attribute --&gt;
<label>Email</label>
<input type="email" id="email">`} />
                <CodeBlock language="html" code={`<!-- Include required attributes --&gt;
<img src="photo.jpg" alt="Description">

<!-- Label linked to input --&gt;
<label for="email">Email</label>
<input type="email" id="email">`} />

                <h3>Error 6: Invalid Attributes</h3>
                <CodeBlock className="error" language="html" code={`<!--  Invalid attributes --&gt;
<div onclick="alert('Hi')"></div>  <!-- Use button --&gt;
<img src="photo.jpg" onclick="...">  <!-- Images aren't interactive --&gt;

<!--  Obsolete attributes --&gt;
<body bgcolor="white">
<font color="red">Text</font>`} />
                <CodeBlock language="html" code={`<!-- Valid attributes --&gt;
<button onclick="alert('Hi')">Click</button>

<!-- Modern CSS --&gt;
<body style="background-color: white;">
<span style="color: red;">Text</span>`} />
            </div>

            <div className="content-section">
                <h2>Browser DevTools for Validation</h2>

                <h3>Chrome DevTools</h3>
                <ol>
                    <li>Press <kbd>F12</kbd> or right-click  Inspect</li>
                    <li>Check <strong>Console</strong> tab for errors</li>
                    <li>Check <strong>Issues</strong> tab for warnings</li>
                    <li>Use <strong>Elements</strong> tab to inspect HTML</li>
                </ol>

                <h3>Common Console Errors:</h3>
                <CodeBlock language="bash" code={`// Unclosed tag
"Unexpected end tag"

// Duplicate ID
"Duplicate ID found"

// Invalid nesting
"Cannot nest <div> inside <span>"

// Missing closing tag
"End tag missing for <div>"`} />
            </div>

            <div className="content-section">
                <h2>HTML Linters</h2>

                <h3>HTMLHint (Command Line)</h3>
                <CodeBlock language="bash" code={`# Install
npm install -g htmlhint

# Validate single file
htmlhint index.html

# Validate all HTML files
htmlhint **/*.html

# Custom configuration (.htmlhintrc)
{
  "tagname-lowercase": true,
  "attr-lowercase": true,
  "attr-value-double-quotes": true,
  "doctype-first": true,
  "tag-pair": true,
  "spec-char-escape": true,
  "id-unique": true,
  "src-not-empty": true,
  "attr-no-duplication": true,
  "title-require": true
}`} />

                <h3>VS Code Extensions</h3>
                <ul>
                    <li><strong>HTMLHint:</strong> Real-time HTML validation</li>
                    <li><strong>W3C Validation:</strong> Validate with W3C validator</li>
                    <li><strong>Webhint:</strong> Comprehensive linting</li>
                    <li><strong>HTML CSS Support:</strong> Class/ID validation</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Accessibility Validation</h2>

                <h3>WAVE (Web Accessibility Evaluation Tool)</h3>
                <p>URL: <strong>https://wave.webaim.org/</strong></p>
                <ul>
                    <li>Checks WCAG compliance</li>
                    <li>Identifies accessibility errors</li>
                    <li>Provides fix suggestions</li>
                    <li>Available as browser extension</li>
                </ul>

                <h3>axe DevTools</h3>
                <ul>
                    <li>Chrome/Firefox extension</li>
                    <li>Automated accessibility testing</li>
                    <li>Detailed error descriptions</li>
                    <li>Guided fixes</li>
                </ul>

                <h3>Common Accessibility Errors:</h3>
                <CodeBlock className="error" language="html" code={`<!--  Missing alt text --&gt;
<img src="photo.jpg">

<!--  Low contrast text --&gt;
<div style="color: #ccc; background: #ddd;">Text</div>

<!--  Missing form labels --&gt;
<input type="text" name="email">

<!--  Empty links --&gt;
<a href="/page"></a>

<!--  Skipped heading levels --&gt;
<h1>Title</h1>
<h4>Subtitle</h4>`} />
            </div>

            <div className="content-section">
                <h2>SEO Validation</h2>

                <h3>Google Lighthouse</h3>
                <ol>
                    <li>Open Chrome DevTools (<kbd>F12</kbd>)</li>
                    <li>Click <strong>Lighthouse</strong> tab</li>
                    <li>Select categories (SEO, Accessibility, Performance)</li>
                    <li>Click <strong>Generate report</strong></li>
                </ol>

                <h3>SEO Checklist:</h3>
                <ul>
                    <li> Title tag present and unique</li>
                    <li> Meta description present</li>
                    <li> One H1 per page</li>
                    <li> Images have alt text</li>
                    <li> Links have descriptive text</li>
                    <li> Viewport meta tag present</li>
                    <li> Valid language attribute</li>
                    <li> Mobile-friendly</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Automated Testing</h2>

                <h3>CI/CD Integration</h3>
                <CodeBlock language="bash" code={`# package.json
{
  "scripts": {
    "validate": "htmlhint src/**/*.html",
    "test:a11y": "pa11y-ci '**/*.html'",
    "test": "npm run validate && npm run test:a11y"
  }
}

# .github/workflows/validate.yml
name: Validate HTML
on: [push, pull_request]
jobs:
  validate:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      - name: Validate HTML
        run: |
          npm install -g htmlhint
          htmlhint **/*.html`} />
            </div>

            <div className="content-section">
                <h2>Cross-Browser Testing</h2>

                <h3>Testing Tools:</h3>
                <ul>
                    <li><strong>BrowserStack:</strong> Test on real devices/browsers</li>
                    <li><strong>LambdaTest:</strong> Cross-browser testing platform</li>
                    <li><strong>Sauce Labs:</strong> Automated testing</li>
                    <li><strong>Can I Use:</strong> Check feature support (caniuse.com)</li>
                </ul>

                <h3>Manual Testing Checklist:</h3>
                <ul>
                    <li> Chrome (latest)</li>
                    <li> Firefox (latest)</li>
                    <li> Safari (latest)</li>
                    <li> Edge (latest)</li>
                    <li> Mobile Safari (iOS)</li>
                    <li> Chrome Mobile (Android)</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Debugging HTML Issues</h2>

                <h3>Technique 1: Comment Out Code</h3>
                <CodeBlock language="html" code={`<!-- Narrow down the problem --&gt;
<div class="container">
    <p>Working content</p>
    
    <!--
    <div class="problematic">
        This might be causing issues
    </div>
    --&gt;
    
    <p>More working content</p>
</div>`} />

                <h3>Technique 2: Validate Step by Step</h3>
                <ol>
                    <li>Start with minimal valid HTML</li>
                    <li>Add sections one by one</li>
                    <li>Validate after each addition</li>
                    <li>Identify which section breaks validation</li>
                </ol>

                <h3>Technique 3: Use DevTools</h3>
                <ul>
                    <li>Inspect element structure</li>
                    <li>Check computed styles</li>
                    <li>View console errors</li>
                    <li>Test DOM manipulation</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Validation Workflow</h2>

                <div className="workflow-steps">
                    <h3>Development</h3>
                    <ol>
                        <li>Use editor with live linting (VS Code + HTMLHint)</li>
                        <li>Fix errors as you code</li>
                        <li>Use Prettier for auto-formatting</li>
                    </ol>

                    <h3>Before Commit</h3>
                    <ol>
                        <li>Run HTML linter locally</li>
                        <li>Check browser console for errors</li>
                        <li>Test in multiple browsers</li>
                    </ol>

                    <h3>Before Deployment</h3>
                    <ol>
                        <li>Run W3C validator</li>
                        <li>Run accessibility checker (axe, WAVE)</li>
                        <li>Run Lighthouse audit</li>
                        <li>Test on real devices</li>
                    </ol>

                    <h3>CI/CD Pipeline</h3>
                    <ol>
                        <li>Automated HTML validation</li>
                        <li>Automated accessibility tests</li>
                        <li>Fail build if errors found</li>
                        <li>Generate validation report</li>
                    </ol>
                </div>
            </div>

            <div className="content-section">
                <h2>Validation Best Practices</h2>

                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Validate during development</li>
                            <li>Fix errors immediately</li>
                            <li>Use automated tools</li>
                            <li>Test in multiple browsers</li>
                            <li>Check accessibility</li>
                            <li>Use linting in editor</li>
                            <li>Validate before deployment</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Ignore validation warnings</li>
                            <li>Test only in one browser</li>
                            <li>Skip accessibility checks</li>
                            <li>Validate only at the end</li>
                            <li>Deploy with errors</li>
                            <li>Ignore console errors</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Quick Validation Checklist</h2>

                <div className="checklist">
                    <h3>Basic Validation</h3>
                    <ul>
                        <li> DOCTYPE declared</li>
                        <li> lang attribute on html tag</li>
                        <li> charset declared in first 1024 bytes</li>
                        <li> Title tag present and unique</li>
                        <li> All tags properly closed</li>
                        <li> Proper nesting of elements</li>
                        <li> No duplicate IDs</li>
                        <li> All required attributes present</li>
                    </ul>

                    <h3>Accessibility</h3>
                    <ul>
                        <li> Alt text on all images</li>
                        <li> Labels on form inputs</li>
                        <li> Proper heading hierarchy</li>
                        <li> Semantic HTML elements</li>
                        <li> Keyboard accessible</li>
                        <li> Sufficient color contrast</li>
                    </ul>

                    <h3>SEO</h3>
                    <ul>
                        <li> Meta description present</li>
                        <li> One H1 per page</li>
                        <li> Descriptive link text</li>
                        <li> Viewport meta tag</li>
                        <li> Mobile-friendly</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLValidation;




